import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../components/Headings";
import PageLayout from "../../components/PageLayout/PageLayout";
import PhoneLayout from "../../components/PhoneLayout/PhoneLayout";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { registerUser } from "../../services/authService";
import { SitePage } from "../../types/SitePage";
import { debugLogError } from "../../utils/debug-log-error";
import "./RegistrationPage.scss";

interface RegisterError {
  code?: string;
  message: string;
}

const RegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAppAuthState();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<RegisterError | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/map");
    }
  }, [isLoggedIn, navigate]);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    []
  );

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setLoading(true);

      try {
        const result = await registerUser(email, password);
        if ("uid" in result) {
          navigate("/map");
        } else {
          setError(result);
        }
      } catch (err: any) {
        debugLogError("RegistrationPage", err);
        setError({
          message: "An unexpected error occurred. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [email, password, navigate]
  );

  const errorMessage = useMemo(() => {
    if (error) {
      if (error.code === "auth/email-already-in-use") {
        return "Email already in use. Please use a different email.";
      }
      if (error.code === "auth/invalid-email") {
        return "Invalid email. Please enter a valid email address.";
      }
      if (error.code === "auth/weak-password") {
        return "Password is too weak. Please enter a stronger password.";
      }
      return new Error(error.message);
    }
    return null;
  }, [error]);

  return (
    <PageLayout page={SitePage.Registration} title="Register">
      <PhoneLayout className="registration-page">
        <Headings.PhoneMain>Register</Headings.PhoneMain>
        <form onSubmit={handleSubmit}>
          {errorMessage && <ErrorDisplay error={errorMessage} />}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={email}
              onChange={handleEmailChange}
              required
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={handlePasswordChange}
              required
              disabled={loading}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Registering..." : "Register"}
          </button>
        </form>
      </PhoneLayout>
    </PageLayout>
  );
};

export default RegistrationPage;
